import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { v4 } from 'uuid';
import { Router, RouterModule } from '@angular/router';
import { LocaleService } from 'src/app/services/locale';
import { CompanyService } from 'src/app/services/company';
import { InputComponent } from '../../shared/input';
import { ButtonComponent } from '../../shared/button';
import { BoltComponent } from '../../icons/bolt';
import { PencilComponent } from '../../icons/pencil';
import { CalculationService } from 'src/app/services/calculation';
import { InboxComponent } from '../../icons/inbox';
import { getUrl } from '@aws-amplify/storage';
import { UserService } from 'src/app/services/user';
import { CategoryService } from 'src/app/services/category';

@Component({
  selector: 'app-calculations',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    InputComponent,
    ButtonComponent,
    BoltComponent,
    PencilComponent,
    InboxComponent
  ],
  template: `
    <div class="px-2 sm:px-0">
      <!-- Header Section (Collapsible) -->
      <div class="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
        <button
          (click)="isHeaderCollapsed = !isHeaderCollapsed"
          class="w-full flex items-center justify-between p-3 sm:p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
        >
          <h1 class="text-lg sm:text-xl futura text-[#043B22]">{{ locale.carbon.heading }}</h1>
          <svg
            class="w-5 h-5 text-[#043B22] transition-transform duration-300"
            [ngClass]="{ 'rotate-180': !isHeaderCollapsed }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div
          class="overflow-hidden transition-all duration-300 ease-in-out"
          [ngClass]="isHeaderCollapsed ? 'max-h-0' : 'max-h-[500px]'"
        >
          <div class="px-3 sm:px-4 pb-3 sm:pb-4 text-xs sm:text-sm text-[#616161] leading-relaxed">
            <p class="mb-3">{{ locale.carbon.subheading }}</p>
            <ul class="list-disc ml-3 sm:ml-5 space-y-2.5 sm:space-y-3">
              <li>
                <span class="font-bold futura text-[#043B22]">
                  {{ locale.carbon.subpoint1 }}:
                </span>
                <span class="ml-1">{{ locale.carbon.subpoint1_data }}</span>
              </li>
              <li>
                <span class="font-bold futura text-[#043B22]">
                  {{ locale.carbon.subpoint2 }}:
                </span>
                <span class="ml-1">{{ locale.carbon.subpoint2_data }}</span>
              </li>
              <li>
                <span class="font-bold futura text-[#043B22]">
                  {{ locale.carbon.subpoint3 }}:
                </span>
                <span class="ml-1">{{ locale.carbon.subpoint3_data }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Select Company Warning -->
      <div *ngIf="!selectedCompany" class="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-xl">
        <p class="text-xs sm:text-sm text-orange-700">
          {{ localeService.getSelectCompanyMessage('calculations', hasCompanies) }}
        </p>
      </div>

      <!-- New Calculation Section (only show when company selected, invited users limited to 1) -->
      <div *ngIf="selectedCompany && !(isInvitedUser && calculations?.length >= 1)" class="mt-4 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl">
        <!-- Section Header -->
        <div class="p-3 sm:p-4 lg:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
          <div class="flex items-center gap-3">
            <div>
              <h2 class="text-sm sm:text-base lg:text-lg futura text-[#043B22]">{{ locale.company_item.heading }}</h2>
              <p class="text-[10px] sm:text-xs lg:text-sm text-[#616161]">{{ locale.company_item.subheading }}</p>
            </div>
          </div>
        </div>

        <!-- Form Content -->
        <div class="p-3 sm:p-4 lg:p-5">
          <form [formGroup]="form" (submit)="submit()" class="flex flex-col sm:flex-row gap-3 sm:items-start">
            <div class="flex-1 max-w-md">
              <app-input
                label="{{ locale.company_item.lbl_calculation_name }}"
                placeholder="{{ locale.company_item.placeholder_calculation_name }}"
                [control]="form.controls['name']"
              ></app-input>
            </div>
            <div class="sm:mt-[18px]">
              <app-button
                [disabled]="form.invalid"
                text="{{ locale.company_item.button_start_calculation }}"
              >
                <app-bolt></app-bolt>
              </app-button>
            </div>
          </form>
        </div>
      </div>

      <!-- Calculations List Section (only show when company selected) -->
      <div *ngIf="selectedCompany" class="mt-4 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl">
        <!-- Section Header -->
        <div class="p-3 sm:p-4 lg:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
          <div class="flex items-center gap-3">
            <div>
              <h2 class="text-sm sm:text-base lg:text-lg futura text-[#043B22]">{{ locale.company_item.table_heading }}</h2>
              <p class="text-[10px] sm:text-xs lg:text-sm text-[#616161]">{{ locale.company_item.table_subheading }}</p>
            </div>
          </div>
        </div>

        <!-- Desktop Table View -->
        <div class="hidden lg:block overflow-hidden">
          <!-- Table Header -->
          <div class="grid grid-cols-12 bg-[#043B22] text-white text-xs lg:text-sm font-medium">
            <div class="col-span-4 px-4 lg:px-6 py-3 lg:py-4">{{ locale.company_item.table_col_name }}</div>
            <div class="col-span-2 px-4 lg:px-6 py-3 lg:py-4">{{ locale.company_item.table_col_date }}</div>
            <div class="col-span-2 px-4 lg:px-6 py-3 lg:py-4">{{ locale.company_item.table_col_category }}</div>
            <div class="col-span-2 px-4 lg:px-6 py-3 lg:py-4">{{ locale.company_item.table_col_completion }}</div>
            <div class="col-span-2 px-4 lg:px-6 py-3 lg:py-4">{{ locale.company_item.table_col_action }}</div>
          </div>

          <!-- Table Rows -->
          <div
            *ngFor="let calculation of calculations; let i = index"
            class="grid grid-cols-12 items-center text-xs lg:text-sm border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors"
            [ngClass]="{ 'bg-gray-50/30': i % 2 === 1 }"
          >
            <div class="col-span-4 px-4 lg:px-6 py-3 lg:py-4 font-medium text-[#212121] truncate">
              {{ calculation.name }}
            </div>
            <div class="col-span-2 px-4 lg:px-6 py-3 lg:py-4 text-[#616161]">
              {{ formatDate(calculation.date) }}
            </div>
            <div class="col-span-2 px-4 lg:px-6 py-3 lg:py-4">
              <span class="inline-flex px-2 py-0.5 bg-[#043B22]/10 text-[#043B22] rounded text-[10px] lg:text-xs font-medium">
                {{ calculation.scope }}
              </span>
            </div>
            <div class="col-span-2 px-4 lg:px-6 py-3 lg:py-4">
              <div class="flex items-center gap-2">
                <div class="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    class="h-full bg-[#76AA84] rounded-full transition-all"
                    [style.width]="calculation.percent?.replace(' ', '')"
                  ></div>
                </div>
                <span class="text-[10px] lg:text-xs text-[#616161] min-w-8">{{ calculation.percent }}</span>
              </div>
            </div>
            <div class="col-span-2 px-4 lg:px-6 py-3 lg:py-4 flex">
              <button
                (click)="resume(calculation.id, calculation.name, calculation.answers, calculation.paidForLogo, calculation.paidForReport)"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] lg:text-xs font-medium text-[#043B22] bg-[#043B22]/5 hover:bg-[#043B22]/10 rounded-lg transition-colors cursor-pointer"
              >
                <app-pencil></app-pencil>
                {{ locale.common.button_edit }}
              </button>
            </div>
          </div>
        </div>

        <!-- Mobile/Tablet Card View -->
        <div class="lg:hidden divide-y divide-gray-100">
          <div
            *ngFor="let calculation of calculations"
            class="p-3 sm:p-4 hover:bg-gray-50/50 transition-colors"
          >
            <div class="flex justify-between items-start mb-2">
              <div class="flex-1 min-w-0">
                <h3 class="text-xs sm:text-sm font-medium text-[#043B22] futura truncate">{{ calculation.name }}</h3>
                <p class="text-[10px] sm:text-xs text-[#616161] mt-0.5">{{ formatDate(calculation.date) }}</p>
              </div>
              <span class="ml-2 px-2 py-0.5 bg-[#043B22]/10 text-[#043B22] rounded text-[9px] sm:text-[10px] font-medium flex-shrink-0">
                {{ calculation.scope }}
              </span>
            </div>

            <div class="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
              <div class="flex items-center gap-2">
                <span class="text-[9px] sm:text-[10px] text-[#616161] uppercase tracking-wide">{{ locale.company_item.table_col_completion }}:</span>
                <div class="flex items-center gap-1.5">
                  <div class="w-14 sm:w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-[#76AA84] rounded-full"
                      [style.width]="calculation.percent?.replace(' ', '')"
                    ></div>
                  </div>
                  <span class="text-[9px] sm:text-[10px] font-medium text-[#043B22]">{{ calculation.percent }}</span>
                </div>
              </div>
              <button
                (click)="resume(calculation.id, calculation.name, calculation.answers, calculation.paidForLogo, calculation.paidForReport)"
                class="inline-flex items-center gap-1 px-2.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-medium text-white bg-[#043B22] hover:bg-[#043B22]/90 rounded-lg transition-colors cursor-pointer"
              >
                <app-pencil color="white"></app-pencil>
                {{ locale.common.button_return }}
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="calculations?.length < 1" class="flex flex-col items-center justify-center py-12 sm:py-16">
          <div class="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
            <app-inbox color="#9CA3AF"></app-inbox>
          </div>
          <p class="text-xs sm:text-sm text-[#616161]">{{ locale.common.noData }}</p>
          <p class="text-[10px] sm:text-xs text-[#9CA3AF] mt-1 p-1">{{ locale.company_item.subheading }}</p>
        </div>
      </div>

      <!-- Bottom Spacing -->
      <div class="h-8 sm:h-12"></div>
    </div>
  `
})
export class CalculationsComponent {
  router = inject(Router);
  userService = inject(UserService);
  localeService = inject(LocaleService);
  companyService = inject(CompanyService);
  calculationService = inject(CalculationService);
  categoryService = inject(CategoryService);

  locale = this.localeService.locale();
  company = this.companyService.company();
  calculations = this.calculationService.calculations();
  selectedCompany: any = null;
  hasCompanies = false;

  isHeaderCollapsed = true;
  isInvitedUser = localStorage.getItem('isInvitedUser') === 'true';

  form = new FormGroup({
    id: new FormControl(v4()),
    name: new FormControl('', Validators.required),
    date: new FormControl(new Date().toISOString()),
    scope: new FormControl('Category 1'),
    percent: new FormControl('0 %'),
    answers: new FormControl('')
  });

  constructor() {
    effect(() => {
      this.locale = this.localeService.locale();
      this.company = this.companyService.company();
      this.calculations = this.calculationService.calculations();
      this.selectedCompany = this.companyService.company();
      this.hasCompanies = this.companyService.companies()?.length > 0;

      // Invited users with active subscription get full access
      const user = this.userService.user();
      this.isInvitedUser = localStorage.getItem('isInvitedUser') === 'true' && !user?.subscriptionPlan;
    });
  }

  ngOnInit() {
    localStorage.removeItem('categories');
    localStorage.removeItem('calculationId');
  }

  getS3Url = async (currentUser: any) => {
    try {
      const fileUrl = await getUrl({ path: currentUser.profilePhoto });
      return fileUrl.url.toString();
    } catch (error) {
      return '/images/welcome.png';
    }
  };

  resume = async (
    id: string,
    name: string,
    answers: any,
    paidForLogo: any,
    paidForReport: any
  ) => {
    localStorage.setItem('calculationId', id);
    localStorage.setItem('calculationName', name);
    localStorage.setItem(
      'companyLogo',
      this.company.companyLogo?.length > 0
        ? this.company.companyLogo
        : '/images/welcome.png'
    );
    localStorage.setItem(
      'profilePhoto',
      String(await this.getS3Url(this.userService.user()))
    );
    const categories = this.storeCalculationAnswers(answers);
    this.setResumeNavigation(categories);
    this.router.navigateByUrl(`/calculation`);
  };

  private storeCalculationAnswers(answers: any): any[] | null {
    localStorage.removeItem('categories');

    if (!answers || typeof answers !== 'string') {
      return null;
    }

    if (answers.length > 2_000_000) {
      console.warn('Skipping oversized saved calculation answers.');
      return null;
    }

    try {
      const parsedAnswers = JSON.parse(answers);
      if (Array.isArray(parsedAnswers)) {
        return this.categoryService.persistCategoriesImmediately(parsedAnswers)
          ? parsedAnswers
          : null;
      }
    } catch (error) {
      console.error('Failed to load saved calculation answers:', error);
    }

    return null;
  }

  private setResumeNavigation(categories: any[] | null): void {
    const fallback = { category: 0, question: 0, viewState: 'question' };
    const target = this.findResumeTarget(categories) ?? fallback;

    localStorage.setItem('currentCategory', String(target.category));
    localStorage.setItem('currentQuestion', String(target.question));
    localStorage.setItem('currentViewState', target.viewState);
  }

  private findResumeTarget(categories: any[] | null):
    | { category: number; question: number; viewState: string }
    | null {
    if (!Array.isArray(categories)) {
      return null;
    }

    for (let categoryIndex = 0; categoryIndex < categories.length; categoryIndex++) {
      const questions = categories[categoryIndex]?.questions;
      if (!Array.isArray(questions)) {
        continue;
      }

      for (let questionIndex = 0; questionIndex < questions.length; questionIndex++) {
        const question = questions[questionIndex];
        if (question?.hidden || (categoryIndex === 1 && questionIndex === 0)) {
          continue;
        }

        if (question?.done !== true) {
          return {
            category: categoryIndex,
            question: questionIndex,
            viewState: 'question',
          };
        }
      }
    }

    return { category: categories.length, question: 0, viewState: 'summary' };
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    // If it's an ISO string, format it to dd/MM/yyyy
    if (dateStr.includes('T') || dateStr.includes('-')) {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    // Handle slash-separated dates
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const first = parseInt(parts[0], 10);
      const second = parseInt(parts[1], 10);
      const year = parts[2];

      // If first > 12, it's DD/MM/YYYY (already correct format)
      if (first > 12) {
        const day = String(first).padStart(2, '0');
        const month = String(second).padStart(2, '0');
        return `${day}/${month}/${year}`;
      }
      // If second > 12, it's MM/DD/YYYY (need to swap)
      if (second > 12) {
        const day = String(second).padStart(2, '0');
        const month = String(first).padStart(2, '0');
        return `${day}/${month}/${year}`;
      }
      // Ambiguous (both <= 12), assume MM/DD/YYYY and swap
      const day = String(second).padStart(2, '0');
      const month = String(first).padStart(2, '0');
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  }

  submit = async () => {
    if (this.form.invalid) return;

    const calculation = await this.calculationService.createCalculation(
      this.form
    );

    this.form.reset();
    this.form.controls.id.setValue(v4());
    this.form.controls.date.setValue(new Date().toISOString());
    localStorage.setItem('calculationId', calculation.id);
    localStorage.setItem(
      'companyLogo',
      this.company.companyLogo?.length > 0
        ? this.company.companyLogo
        : '/images/welcome.png'
    );
    localStorage.setItem(
      'profilePhoto',
      String(await this.getS3Url(this.userService.user()))
    );
    this.router.navigateByUrl(`/calculation`);
  };
}
